import React, { Dispatch, memo, SetStateAction, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { Input, InputType, SpinnerOverlay, SubmissionButtons, TextArea } from '@components/common';

import { isEmpty } from '@utils/common';

import { TValueOfStringRecord } from '@interfaces/common/genericWithModifying';
import { ICrowdCellsConfigPreset } from '@interfaces/crowdcell/crowdCells';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { getSchema } from './schema';

import styles from './CreateConfigPreset.module.scss';

export const ECreateConfigPresetFields = {
	name: 'name',
	comment: 'comment',
} as const;

export type TCreateConfigPresetFields = TValueOfStringRecord<typeof ECreateConfigPresetFields>

export interface ICreateConfigPresetForm {
	[ECreateConfigPresetFields.name]: string;
	[ECreateConfigPresetFields.comment]?: string;
}

export interface ICreateConfigPresetProps {
	onSave: (values: { name: string; comment?: string }) => void;
	cancel: () => void;
	presets: ICrowdCellsConfigPreset[];
	isLoading: boolean;
	setIsDirtyForm: Dispatch<SetStateAction<boolean>>;
	preset: ICrowdCellsConfigPreset | null;
}

function CreateConfigPreset({
	onSave,
	cancel,
	presets = [],
	isLoading,
	setIsDirtyForm,
	preset,
}: ICreateConfigPresetProps) {
	const schema = useMemo(() => {
		return getSchema(presets, !!preset);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [presets.length]);

	const {
		handleSubmit,
		setValue,
		register,
		formState: {
			errors,
			isDirty,
		},
	} = useForm<ICreateConfigPresetForm>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			name: preset?.name || '',
			comment: preset?.comment || '',
		},
	});

	useEffect(() => {
		setIsDirtyForm(isDirty);
	}, [isDirty, setIsDirtyForm]);

	const onSubmit = (values: { name: string; comment?: string }) => {
		onSave(values);
	};

	return (
		<div className={styles.createConfigPreset}>
			<div className={styles.createConfigPreset__form}>
				<div className={styles.createConfigPreset__form__input}>
					<Input
						errorMessage={errors.name?.message}
						isError={!!errors.name?.message}
						label='Preset Name'
						name={ECreateConfigPresetFields.name}
						placeholder='Enter preset name'
						register={register}
						required
						setValue={setValue}
						type={InputType.text}
						withUseForms
					/>
				</div>
				<div className={styles.createConfigPreset__form__input}>
					<TextArea
						errorMessage={errors.comment?.message}
						label='Comment'
						name={ECreateConfigPresetFields.comment}
						placeholder='Enter comment to preset'
						register={register}
						rows={3}
						withUseForms
					/>
				</div>
			</div>
			<SubmissionButtons
				disabledPositive={!isEmpty(errors)}
				isNegativeButtonNeeded
				isPositiveButtonNeeded
				negativeHandler={cancel}
				negativeLabel='Cancel'
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Save'
			/>
			{isLoading && <SpinnerOverlay />}
		</div>
	);
}

export default memo(CreateConfigPreset);
